import { CloseOutlined } from "@ant-design/icons";
import type { RadioChangeEvent } from "antd";
import { Flex, Typography } from "antd";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppContext } from "../../../context/AppContext";
import { mapDataToOption } from "../../utils/CommonVessels";
import { DEFAULT_BUSINESS_DAYS } from "../../../utils/businessHourUtils";
import BusinessHour from "../../../voice/components/Nodes/BusinessHour";
import { ConfigPanelFooter } from "./ConfigPanelFooter";
import { ConfigPanelShell } from "./ConfigPanelShell";
import { StepTypePicker } from "./StepTypePicker";
import HInput from "../../../components/custom/input/input/Input";
import HRadioCardGroup from "../../../components/custom/input/radio/HRadioCardGroup";
import HRadioGroup from "../../../components/custom/input/radio/HRadioGroup";
import HSelect from "../../../components/custom/input/select/Select";
import { countries } from "../../../constants/AppConstants";
import type { ConditionNodeData } from "../nodes/ConditionNode";
import type { WorkflowEnumItem, WorkflowEnumOption } from "../hooks/useWorkflowEnums";
import {
    conditionCodeToTitle,
    conditionCodeToView,
    getConditionIconAndClass,
    type ConditionView,
} from "../utils/conditionCodeMapping";
import { CONDITION_HELP_CONTENT } from "../utils/helpContentConfig";
import { useWorkflowStore } from "../store";
import { Trigger } from "../nodes/TriggerNode";
import { selectSelectedStep, selectSelectedStepIndex } from "../store/selectors";
import { useStepConfigPanel } from "../hooks/useStepConfigPanel";

const { Text } = Typography;

type ConditionViewWithPicker = ConditionView | "picker";

export interface ConditionConfigPanelProps {
    conditionTypes?: WorkflowEnumItem[];
    savedNumbers?: Array<{ phoneNumber?: string; friendlyName?: string; teamName?: string; integId?: string }>;
    scheduleOptions?: WorkflowEnumOption[];
    isNarrowLayout: boolean;
}

const RUN_SCHEDULE_OPTIONS = [
    { value: "BusinessHours", label: "Business Hours", tag: "Run during your business hours" },
    { value: "OutsideBusinessHours", label: "Outside Business Hours", tag: "Run outside your business hours" },
    { value: "SpecificHours", label: "Specific Hours", tag: "Run during a custom time range" },
];

const RUN_SCHEDULE_BY_VALUE = new Map(RUN_SCHEDULE_OPTIONS.map((o) => [o.value, o]));

function mapScheduleOptionsForRadio(
    scheduleOptions: WorkflowEnumOption[]
): Array<{ value: string; label: string; tag?: string }> {
    let rows: Array<{ value: string; label: string; tag?: string }>;

    if (!scheduleOptions.length) {
        rows = [...RUN_SCHEDULE_OPTIONS];
    } else {
        rows = scheduleOptions.map((opt) => {
            const fallback = RUN_SCHEDULE_BY_VALUE.get(opt.value);
            const label = (opt.label?.trim() || fallback?.label || opt.value).trim();
            const desc = typeof opt.desc === "string" ? opt.desc.trim() : "";

            let tag: string | undefined;
            if (desc && desc.toLowerCase() !== label.toLowerCase()) {
                tag = desc;
            } else if (fallback?.tag && fallback.tag.toLowerCase() !== label.toLowerCase()) {
                tag = fallback.tag;
            }

            const row: { value: string; label: string; tag?: string } = { value: opt.value, label };
            if (tag) row.tag = tag;
            return row;
        });
    }

    return rows;
}

type SelectOption = { value: string; label?: string; labelText?: string };

function parseKeywordInput(raw: string): string[] {
    if (!raw?.trim()) return [];
    return raw
        .split(/[,，;]+/)
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);
}

const KeywordsConditionForm: React.FC<{
    data: ConditionNodeData;
    update: (partial: Partial<ConditionNodeData>) => void;
    registerBeforeCommit?: (fn: (() => Partial<ConditionNodeData> | undefined) | null) => void;
    onPendingInputChange?: (hasPending: boolean) => void;
}> = ({ data, update, registerBeforeCommit, onPendingInputChange }) => {
    const [keywordInputValue, setKeywordInputValue] = useState("");
    const inputValueRef = useRef(keywordInputValue);
    const radioGroupRef = useRef<HTMLDivElement>(null);
    inputValueRef.current = keywordInputValue;

    useEffect(() => {
        const hasPending = parseKeywordInput(keywordInputValue).length > 0;
        onPendingInputChange?.(hasPending);
        return () => onPendingInputChange?.(false);
    }, [keywordInputValue, onPendingInputChange]);

    const mergeKeywordsFromParsed = useCallback(
        (values: string[]): Partial<ConditionNodeData> | undefined => {
            if (values.length === 0) return undefined;
            const currentKeywords = data.keywords ?? [];
            const seen = new Set(currentKeywords);
            const toAdd = values.filter((v) => !seen.has(v) && (seen.add(v), true));
            if (toAdd.length === 0) return undefined;
            return { keywords: [...currentKeywords, ...toAdd] };
        },
        [data.keywords]
    );

    const flushKeywordsToTags = useCallback((): Partial<ConditionNodeData> | undefined => {
        const values = parseKeywordInput(inputValueRef.current);
        const partial = mergeKeywordsFromParsed(values);
        if (partial) setKeywordInputValue("");
        return partial;
    }, [mergeKeywordsFromParsed]);

    useEffect(() => {
        registerBeforeCommit?.(flushKeywordsToTags);
        return () => registerBeforeCommit?.(null);
    }, [registerBeforeCommit, flushKeywordsToTags]);

    const handleAddKeyword = () => {
        const values = parseKeywordInput(keywordInputValue);
        if (values.length === 0) return;
        const partial = mergeKeywordsFromParsed(values);
        if (partial) update(partial);
        setKeywordInputValue("");
    };

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (radioGroupRef.current?.contains(e.relatedTarget as Node)) return;
        handleAddKeyword();
    };

    return (
        <Flex gap="middle" vertical>
            <Flex vertical gap="small">
                <Text className="hs-fw-500 hs-fs-12 hs-color-BlackRussian">
                Add Keywords
                </Text>
                <div className="d-flex gap-2 align-items-center">
                    <HInput
                        placeholder="e.g. SUPPORT, HELP"
                        value={keywordInputValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKeywordInputValue(e.target.value)}
                        onBlur={handleInputBlur}
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === "Enter" || e.key === ",") {
                                e.preventDefault();
                                handleAddKeyword();
                            }
                        }}
                        onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                            const text = e.clipboardData.getData("text");
                            const values = parseKeywordInput(text);
                            if (values.length === 0) return;
                            e.preventDefault();
                            const partial = mergeKeywordsFromParsed(values);
                            if (partial) update(partial);
                            setKeywordInputValue("");
                        }}
                    />
                </div>
                <Text className="hs-fs-12 hs-color-mediumDark">
                    Messages containing these keywords will trigger this rule.
                </Text>
                {(data.keywords?.length ?? 0) > 0 && (
                    <div className="d-flex flex-wrap gap-1 mt-2">
                        {(data.keywords ?? []).map((keyword, keywordIndex) => (
                            <span
                                key={`kw-${keywordIndex}-${keyword}`}
                                className="hs-removable-tag d-inline-flex position-relative mw-100 align-top rounded-2 border fw-medium hs-fs-12"
                            >
                                <span className="hs-removable-tag-label">{keyword}</span>
                                <button
                                    type="button"
                                    className="hs-removable-tag-remove d-inline-flex align-items-center justify-content-center"
                                    onClick={() =>
                                        update({ keywords: (data.keywords ?? []).filter((k) => k !== keyword) })
                                    }
                                    aria-label={`Remove ${keyword}`}
                                >
                                    <CloseOutlined />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </Flex>
            <div ref={radioGroupRef}>
            <HRadioGroup
                groupLabel="Match Type"
                options={[
                    { value: "Contains", label: "Contains", tag: "Keyword appears anywhere in the message." },
                    { value: "Exact", label: "Exact", tag: "Message exactly matches the keyword." },
                    { value: "StartsWith", label: "Starts With", tag: "Message starts with the keyword.." },
                ]}
                radioGroupProps={{
                    value: data.matchType ?? "Contains",
                    onChange: (e: { target: { value: string } }) => update({ matchType: e.target.value }),
                    className: "d-flex flex-column gap-2",
                }}
            />
            </div>
        </Flex>
    );
};

const NumbersConditionForm: React.FC<{
    data: ConditionNodeData;
    update: (partial: Partial<ConditionNodeData>) => void;
    numberOptions: SelectOption[];
}> = ({ data, update, numberOptions }) => (
    <Flex gap="small" vertical>
        <Text className="hs-fw-500 hs-fs-12 hs-color-BlackRussian">
            Numbers
        </Text>
        <HSelect
            bordered={false}
            mode="multiple"
            size="large"
            selectSource="numbers"
            enableSearch={false}
            customProps={{
                value: data.phoneNumbers ?? [],
                onChange: (value: string[]) => update({ phoneNumbers: value }),
                showSearch: false,
            }}
            defaultTagRender={{
                onCloseSetState: (valueOrUpdater: string[] | ((prev: string[]) => string[])) => {
                    const newValue =
                        typeof valueOrUpdater === "function"
                            ? valueOrUpdater(data.phoneNumbers ?? [])
                            : valueOrUpdater;
                    update({ phoneNumbers: newValue });
                },
                tagItemToRender: "label",
            }}
            popupMatchSelectWidth={false}
            placeholder="Select Numbers"
            options={numberOptions}
            loading={false}
        />
        <Text className="hs-fs-12 hs-color-mediumDark">
           Limit this rule to messages from selected numbers.
        </Text>
    </Flex>
);

const CountriesConditionForm: React.FC<{
    data: ConditionNodeData;
    update: (partial: Partial<ConditionNodeData>) => void;
    countryOptions: SelectOption[];
}> = ({ data, update, countryOptions }) => (
    <Flex gap="small" vertical>
        <Text className="hs-fw-500 hs-fs-12 hs-color-BlackRussian">
            Countries
        </Text>
        <HSelect
            mode="multiple"
            placeholder="Select Countries"
            size="large"
            selectSource="countries"
            defaultTagRender={{
                onCloseSetState: (valueOrUpdater: string[] | ((prev: string[]) => string[])) => {
                    const newValue =
                        typeof valueOrUpdater === "function"
                            ? valueOrUpdater(data.countries ?? [])
                            : valueOrUpdater;
                    update({ countries: newValue });
                },
            }}
            customProps={{
                value: data.countries ?? [],
                onChange: (val: string[]) => update({ countries: val }),
                className: "w-100",
            }}
            options={countryOptions}
        />
        <Text className="hs-fs-12 hs-color-mediumDark">
           Limit this rule to messages from selected countries.
        </Text>
    </Flex>
);

const ScheduleConditionForm: React.FC<{
    data: ConditionNodeData;
    update: (partial: Partial<ConditionNodeData>) => void;
    scheduleOptions: WorkflowEnumOption[];
    defaultTimeZone: string;
}> = ({ data, update, scheduleOptions, defaultTimeZone }) => {
    const selectedSchedule = data.runSchedule ?? "BusinessHours";
    const scheduleOptionsForRadio = mapScheduleOptionsForRadio(scheduleOptions);

    const businessHourData = {
        isCalendarHours: false,
        timeZone: data.businessHours?.timeZone ?? defaultTimeZone,
        businessDays: data.businessHours?.businessDays ?? DEFAULT_BUSINESS_DAYS,
    };

    const handleBusinessHourChange = (updater: (prev: typeof businessHourData) => typeof businessHourData) => {
        const nextData = updater(businessHourData);
        update({
            businessHours: {
                timeZone: nextData.timeZone,
                businessDays: nextData.businessDays,
            },
        });
    };

    return (
        <div className="px-1 py-1">
            <HRadioCardGroup
                options={scheduleOptionsForRadio}
                radioGroupProps={{
                    value: selectedSchedule,
                    onChange: (e: RadioChangeEvent) => {
                        const scheduleValue = String(e.target.value ?? "");
                        update({ runSchedule: scheduleValue });
                        if (scheduleValue === "SpecificHours" && !data.businessHours) {
                            update({
                                runSchedule: scheduleValue,
                                businessHours: {
                                    timeZone: businessHourData.timeZone,
                                    businessDays: DEFAULT_BUSINESS_DAYS,
                                },
                            });
                        }
                    },
                }}
            />
            {selectedSchedule === "SpecificHours" && (
                <div className="mt-4">
                    <BusinessHour
                        id={undefined}
                        data={undefined}
                        number={undefined}
                        integId={undefined}
                        businessHourSettingLabel={undefined}
                        businessHourData={businessHourData}
                        setBusinessHourData={handleBusinessHourChange}
                        isFromAdminSetting
                        optionsNeeded={false}
                    />
                </div>
            )}
        </div>
    );
};

const OtherConditionForm: React.FC = () => (
    <div className="px-1">
        <Text className="hs-fs-12 hs-color-mediumDark">
            Configuration for this condition type is not yet available.
        </Text>
    </div>
);

function renderConditionFormContent(
    view: ConditionView,
    data: ConditionNodeData,
    update: (partial: Partial<ConditionNodeData>) => void,
    formOptions: {
        numberOptions: SelectOption[];
        countryOptions: SelectOption[];
        scheduleOptions: WorkflowEnumOption[];
        defaultTimeZone: string;
        registerKeywordsBeforeCommit?: (fn: (() => Partial<ConditionNodeData> | undefined) | null) => void;
        onKeywordsPendingInputChange?: (hasPending: boolean) => void;
    }
): React.ReactNode {
    switch (view) {
        case "keywords":
            return (
                <KeywordsConditionForm
                    data={data}
                    update={update}
                    registerBeforeCommit={formOptions.registerKeywordsBeforeCommit}
                    onPendingInputChange={formOptions.onKeywordsPendingInputChange}
                />
            );
        case "numbers":
            return <NumbersConditionForm data={data} update={update} numberOptions={formOptions.numberOptions} />;
        case "countries":
            return (
                <CountriesConditionForm data={data} update={update} countryOptions={formOptions.countryOptions} />
            );
        case "schedule":
            return (
                <ScheduleConditionForm
                    data={data}
                    update={update}
                    scheduleOptions={formOptions.scheduleOptions}
                    defaultTimeZone={formOptions.defaultTimeZone}
                />
            );
        case "other":
            return <OtherConditionForm />;
        default:
            return null;
    }
}

function getConditionTitleForView(view: ConditionView, data: ConditionNodeData): string {
    switch (view) {
        case "keywords":
            return "Keyword";
        case "numbers":
            return "From Number";
        case "countries":
            return "Countries";
        case "schedule":
            return "Run Schedule";
        case "other":
            return data.title ?? "Condition";
        default:
            return "Condition";
    }
}

function buildConditionDraftWithPlaceholder(
    base: ConditionNodeData,
    partial: Partial<ConditionNodeData>
): ConditionNodeData {
    const merged = { ...base, ...partial };

    const hasValidConfig =
        (merged.phoneNumbers && merged.phoneNumbers.length > 0) ||
        (merged.keywords && merged.keywords.length > 0) ||
        (merged.countries && merged.countries.length > 0) ||
        !!merged.runSchedule;

    if (partial.keywords && partial.keywords.length > 0 && !merged.matchType) {
        merged.matchType = "Contains";
    }

    const resolvedTitle = hasValidConfig
        ? (merged.title ?? conditionCodeToTitle(merged.conditionCode) ?? "When to continue")
        : (merged.title ?? conditionCodeToTitle(merged.conditionCode));

    return {
        ...merged,
        isPlaceholder: !hasValidConfig,
        title: resolvedTitle ?? undefined,
    };
}

export const ConditionConfigPanel: React.FC<ConditionConfigPanelProps> = ({
    conditionTypes = [],
    savedNumbers = [],
    scheduleOptions = [],
    isNarrowLayout,
}) => {
    const [appProperties] = useContext(AppContext);

    const selectedStep = useWorkflowStore(selectSelectedStep);
    const selectedStepIndex = useWorkflowStore(selectSelectedStepIndex);
    const updateStepData = useWorkflowStore((s) => s.updateStepData);
    const deleteStep = useWorkflowStore((s) => s.deleteStep);
    const selectNode = useWorkflowStore((s) => s.selectNode);
    const incrementCloseSignal = useWorkflowStore((s) => s.incrementCloseSignal);
    const triggerType = useWorkflowStore((s) => s.triggerType);

    const nodeId = selectedStep?.id ?? "";
    const persistedData = useMemo(
        () => ((selectedStep?.data as ConditionNodeData) ?? {}),
        [selectedStep?.data]
    );

    const { draftData, updateDraft, commitDraft, hasDraftChanges, setInitialData } = useStepConfigPanel<
        ConditionNodeData
    >(nodeId, persistedData, updateStepData, {
        transformDraft: buildConditionDraftWithPlaceholder,
    });

    const [currentView, setCurrentView] = useState<ConditionViewWithPicker>("picker");

    const numberOptions = useMemo(
        () => mapDataToOption(savedNumbers ?? [], appProperties, null, false, false, true) ?? [],
        [savedNumbers, appProperties]
    );
    const countryOptions = useMemo(() => countries.map((c) => ({ ...c, labelText: c.label })), []);
    const defaultTimeZone =
        appProperties?.currentUser?.userTimeZone ??
        (typeof Intl !== "undefined" && Intl.DateTimeFormat
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : "UTC");
    const prevNodeIdRef = useRef(nodeId);
    const keywordsFlushRef = useRef<(() => Partial<ConditionNodeData> | undefined) | null>(null);
    const [hasPendingKeywordInput, setHasPendingKeywordInput] = useState(false);

    useEffect(() => {
        if (prevNodeIdRef.current !== nodeId) {
            prevNodeIdRef.current = nodeId;
            setCurrentView("picker");
            setHasPendingKeywordInput(false);
        }
        if (persistedData.conditionCode) {
            setCurrentView(conditionCodeToView(persistedData.conditionCode));
        }
        const hasKeywords = (persistedData.keywords?.length ?? 0) > 0;
        const hasCountries = (persistedData.countries?.length ?? 0) > 0;
        const hasSchedule = !!persistedData.runSchedule;
        const hasNumbers = (persistedData.phoneNumbers?.length ?? 0) > 0;
        if (hasKeywords) setCurrentView("keywords");
        else if (hasNumbers) setCurrentView("numbers");
        else if (hasCountries) setCurrentView("countries");
        else if (hasSchedule) setCurrentView("schedule");
    }, [
        nodeId,
        persistedData.conditionCode,
        persistedData.keywords?.length,
        persistedData.countries?.length,
        persistedData.phoneNumbers?.length,
        persistedData.runSchedule,
    ]);

    const handleSelectCondition = (code: string, _label: string, _meta?: unknown) => {
        const viewType = conditionCodeToView(code);
        const matchingCondition = conditionTypes.find((ct) => ct.code === code);
        const initialPartial: Partial<ConditionNodeData> = {
            conditionCode: code,
            title: matchingCondition?.idleLabel ?? "When to continue",
            isPlaceholder: true,
        };
        if (viewType === "schedule") {
            initialPartial.runSchedule = "BusinessHours";
        }
        updateDraft(initialPartial);
        setCurrentView(viewType);
    };

    const isPlaceholderStep = (persistedData.isPlaceholder ?? false) || !persistedData.conditionCode;

    const handleCloseDeselect = () => {
        if (isPlaceholderStep && selectedStep?.id) {
            deleteStep(selectedStep.id);
        }
        selectNode(null);
    };

    const handleBackToPicker = () => {
        updateDraft({
            conditionCode: undefined,
            title: "When to continue",
            isPlaceholder: true,
            keywords: undefined,
            matchType: undefined,
            phoneNumbers: undefined,
            countries: undefined,
            runSchedule: undefined,
            businessHours: undefined,
        });
        setCurrentView("picker");
    };

    const hasValidConfigForView =
        currentView === "keywords"
            ? (draftData.keywords?.length ?? 0) > 0 || hasPendingKeywordInput
            : currentView === "numbers"
              ? (draftData.phoneNumbers?.length ?? 0) > 0
              : currentView === "countries"
                ? (draftData.countries?.length ?? 0) > 0
                : currentView === "schedule"
                  ? Boolean(draftData.runSchedule)
                  : Boolean(draftData.conditionCode);
    const canAddStep = hasValidConfigForView && Boolean(draftData.conditionCode);
    const canSaveChanges = hasDraftChanges || (currentView === "keywords" && hasPendingKeywordInput);

    const handlePrimaryClick = () => {
        const keywordsOverride = keywordsFlushRef.current?.();
        const mergedDraft = keywordsOverride ? { ...draftData, ...keywordsOverride } : draftData;
        const canProceedAfterFlush =
            Boolean(mergedDraft.conditionCode) ||
            Boolean(mergedDraft.runSchedule) ||
            (mergedDraft.keywords?.length ?? 0) > 0 ||
            (mergedDraft.phoneNumbers?.length ?? 0) > 0 ||
            (mergedDraft.countries?.length ?? 0) > 0;
        if (isPlaceholderStep) {
            if (!canProceedAfterFlush || selectedStepIndex < 0 || !selectedStep || !nodeId) return;
            const dataToCommit = { ...mergedDraft, isPlaceholder: false };
            updateStepData(nodeId, dataToCommit);
            setInitialData(dataToCommit);
            if (isNarrowLayout) {
                incrementCloseSignal();
                selectNode(null);
            }
            return;
        }
        if (!keywordsOverride && !hasDraftChanges) return;
        commitDraft(keywordsOverride);
        if (isNarrowLayout) {
            incrementCloseSignal();
            selectNode(null);
        }
    };

    const registerKeywordsBeforeCommit = useCallback((fn: (() => Partial<ConditionNodeData> | undefined) | null) => {
        keywordsFlushRef.current = fn;
    }, []);

    const formOptions = {
        numberOptions,
        countryOptions,
        scheduleOptions,
        defaultTimeZone,
        registerKeywordsBeforeCommit,
        onKeywordsPendingInputChange: setHasPendingKeywordInput,
    };

    if (currentView === "picker") {
        return (
            <ConfigPanelShell
                title="Condition"
                onClose={handleCloseDeselect}
                footer={
                    <ConfigPanelFooter backOnly isPlaceholder={isPlaceholderStep} />
                }
            >
                <StepTypePicker
                    items={conditionTypes}
                    onSelect={handleSelectCondition}
                    getIcon={(code) => getConditionIconAndClass(code).icon}
                />
            </ConfigPanelShell>
        );
    }

    const displayTitle = getConditionTitleForView(currentView, draftData);
    const helpContent = CONDITION_HELP_CONTENT[currentView] ?? CONDITION_HELP_CONTENT.other;

    return (
        <ConfigPanelShell
            title={displayTitle}
            onClose={isPlaceholderStep ? handleBackToPicker : handleCloseDeselect}
            helpContent={helpContent}
            footer={
                <ConfigPanelFooter
                    primaryLabel={isPlaceholderStep ? "Apply" : "Save Changes"}
                    primaryDisabled={isPlaceholderStep ? !canAddStep : !canSaveChanges}
                    onPrimaryClick={handlePrimaryClick}
                    isPlaceholder={isPlaceholderStep}
                    hideDelete={triggerType === Trigger.Message.Outbound}
                    onBackToPicker={isPlaceholderStep ? handleBackToPicker : undefined}
                />
            }
        >
            {renderConditionFormContent(currentView, draftData, updateDraft, formOptions)}
        </ConfigPanelShell>
    );
};
